import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Check, Edit, Delete } from '@mui/icons-material';
import { Box } from '@mui/material';
import { StyledGrayIcon, StyledTableRow, StyledTableBodyCell } from 'components/common/Styles';
import { IconButton } from '@totalsoft/rocket-ui';
import { ADMIN_INSURANCE_CONVENTIONS_WRITE } from 'constants/rightsCodes';
import { DELETE_INSURANCE_CONVENTION_ADDITIONAL_DATA } from 'features/insuranceConventions/edit/mutations';
import { editCoefficientVar } from 'features/insuranceConventions/edit/variables';
import { useTranslation } from 'react-i18next';
import { useFieldDecimalPlacesConfig } from 'hooks';
import { usePermission } from 'providers/RightsHooks';
import { useMutation } from 'hooks/apollo/useMutation';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';

const InsuranceConventionCoefficientContainer = ({ coefficient }) => {
  const { cvsChPercent, cvsChAmount } = useFieldDecimalPlacesConfig();
  const { t } = useTranslation();
  const confirm = useConfirmDialog();
  const canWrite = usePermission(ADMIN_INSURANCE_CONVENTIONS_WRITE);
  const [deleting, setDeleting] = useState(false);

  // #region mutations
  const [deleteInsuranceConventionAdditionalData] = useMutation(DELETE_INSURANCE_CONVENTION_ADDITIONAL_DATA, {
    onError: () => {
      setDeleting(false);
    }
  });
  // #endregion

  // #region handlers
  const handleEdit = useCallback(() => {
    editCoefficientVar({ open: true, coefficient: coefficient });
  }, [coefficient]);

  const handleDeleteCoefficient = useCallback(() => {
    confirm(
      () => {
        setDeleting(true);
        deleteInsuranceConventionAdditionalData({
          variables: { insuranceConventionAdditionalDataId: coefficient.insuranceConventionAdditionalDataId }
        });
      },
      {
        title: t('InsuranceConventions.Details.Coefficients.Delete'),
        content: t('InsuranceConventions.Details.Coefficients.DeleteConfirmationConsent')
      }
    );
  }, [confirm, t, deleteInsuranceConventionAdditionalData, coefficient.insuranceConventionAdditionalDataId]);
  // #endregion

  return (
    <>
      <StyledTableRow>
        <StyledTableBodyCell>{coefficient.additionalDataTypeName}</StyledTableBodyCell>
        <StyledTableBodyCell>{coefficient.detailName}</StyledTableBodyCell>
        <StyledTableBodyCell>{coefficient.relativePercent?.toFixed(cvsChPercent)}</StyledTableBodyCell>
        <StyledTableBodyCell>{coefficient.percentFormula}</StyledTableBodyCell>
        <StyledTableBodyCell colSpan={2}>{coefficient.amount?.toFixed(cvsChAmount)}</StyledTableBodyCell>
        <StyledTableBodyCell>{coefficient.amountFormula}</StyledTableBodyCell>
        <StyledTableBodyCell>
          {coefficient.isDefault ? <Check color='success' /> : <StyledGrayIcon>close</StyledGrayIcon>}
        </StyledTableBodyCell>
        <StyledTableBodyCell>
          <Box display='flex' gap={1} alignItems='center' justifyContent='end'>
            <IconButton size='tiny' variant='text' color='primary' onClick={handleEdit} tooltip={t('Edit')}>
              <Edit />
            </IconButton>
            <IconButton
              size='tiny'
              variant='text'
              color='primary'
              onClick={handleDeleteCoefficient}
              tooltip={t('Delete')}
              disabled={!canWrite || deleting}
            >
              <Delete />
            </IconButton>
          </Box>
        </StyledTableBodyCell>
      </StyledTableRow>
    </>
  );
};

InsuranceConventionCoefficientContainer.propTypes = {
  coefficient: PropTypes.object
};
export default InsuranceConventionCoefficientContainer;
